import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../../hooks/useGoogleAnalytics';

const ActionPanel = ({ dealStatus, dealId, onAction, currentLanguage }) => {
  const navigate = useNavigate();
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDetails, setDisputeDetails] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');

  const translations = {
    en: {
      authorizePayment: 'Authorize Payment',
      confirmDelivery: 'Confirm Delivery',
      completeAndRate: 'Complete & Rate',
      flagDispute: 'Flag Dispute',
      cancelDeal: 'Cancel Deal',
      disputeTitle: 'Flag Dispute',
      disputeReason: 'Reason for Dispute',
      disputeDetails: 'Additional Details',
      disputePlaceholder: 'Describe the issue in detail...',
      submitDispute: 'Submit Dispute',
      cancel: 'Cancel',
      ratingTitle: 'Rate Your Experience',
      ratingLabel: 'Your Rating',
      reviewLabel: 'Write a Review',
      reviewPlaceholder: 'Share your experience...',
      submitReview: 'Submit Review',
      itemNotReceived: 'Item Not Received',
      itemDamaged: 'Item Damaged',
      wrongItem: 'Wrong Item',
      qualityIssue: 'Quality Issue',
      communicationIssue: 'Communication Issue',
      other: 'Other'
    },
    ko: {
      authorizePayment: '결제 승인',
      confirmDelivery: '배송 확인',
      completeAndRate: '완료 및 평가',
      flagDispute: '분쟁 신고',
      cancelDeal: '거래 취소',
      disputeTitle: '분쟁 신고',
      disputeReason: '분쟁 사유',
      disputeDetails: '추가 세부정보',
      disputePlaceholder: '문제를 자세히 설명해주세요...',
      submitDispute: '분쟁 제출',
      cancel: '취소',
      ratingTitle: '경험 평가',
      ratingLabel: '평점',
      reviewLabel: '리뷰 작성',
      reviewPlaceholder: '경험을 공유해주세요...',
      submitReview: '리뷰 제출',
      itemNotReceived: '상품 미수령',
      itemDamaged: '상품 손상',
      wrongItem: '잘못된 상품',
      qualityIssue: '품질 문제',
      communicationIssue: '의사소통 문제',
      other: '기타'
    }
  };

  const t = translations?.[currentLanguage];

  const disputeReasons = [
    { value: 'not_received', label: t?.itemNotReceived },
    { value: 'damaged', label: t?.itemDamaged },
    { value: 'wrong_item', label: t?.wrongItem },
    { value: 'quality', label: t?.qualityIssue },
    { value: 'communication', label: t?.communicationIssue },
    { value: 'other', label: t?.other }
  ];

  const handleDisputeSubmit = () => {
    // Track dispute submission
    trackEvent('deal_dispute', {
      deal_id: dealId,
      dispute_reason: disputeReason,
      deal_status: dealStatus,
      page: '/deal-room-page'
    });
    
    onAction('dispute', { reason: disputeReason, details: disputeDetails });
    setShowDisputeModal(false);
    setDisputeReason('');
    setDisputeDetails('');
  };

  const handleRatingSubmit = () => {
    // Track deal completion
    trackEvent('deal_completion', {
      deal_id: dealId,
      rating: rating,
      has_review: review?.length > 0,
      page: '/deal-room-page'
    });
    
    onAction('complete', { rating, review });
    setShowRatingModal(false);
    setRating(0);
    setReview('');
  };

  const getActionButtons = () => {
    switch (dealStatus) {
      case 'accepted':
        return (
          <Button
            variant="default"
            size="lg"
            iconName="CreditCard"
            iconPosition="left"
            fullWidth
            onClick={() => navigate('/payment-screen', { state: { dealId } })}
          >
            {t?.authorizePayment}
          </Button>
        );
      
      case 'in_transit':
        return (
          <Button
            variant="success"
            size="lg"
            iconName="Package"
            iconPosition="left"
            fullWidth
            onClick={() => onAction('confirm_delivery')}
          >
            {t?.confirmDelivery}
          </Button>
        );
      
      case 'delivered':
        return (
          <Button
            variant="success"
            size="lg"
            iconName="Star"
            iconPosition="left"
            fullWidth
            onClick={() => setShowRatingModal(true)}
          >
            {t?.completeAndRate}
          </Button>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <div className="bg-card border-t border-border p-4 md:p-6 lg:p-8">
        <div className="flex flex-col gap-3 md:gap-4">
          {getActionButtons()}

          {dealStatus !== 'completed' && dealStatus !== 'cancelled' && dealStatus !== 'disputed' && (
            <div className="flex gap-3 md:gap-4">
              <Button
                variant="destructive"
                size="lg"
                iconName="AlertTriangle"
                iconPosition="left"
                fullWidth
                onClick={() => setShowDisputeModal(true)}
              >
                {t?.flagDispute}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                iconName="XCircle"
                iconPosition="left"
                fullWidth
                onClick={() => onAction('cancel')}
              >
                {t?.cancelDeal}
              </Button>
            </div>
          )}
        </div>
      </div>
      {showDisputeModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-200 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border border-border shadow-elevation-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                  {t?.disputeTitle}
                </h3>
                <button
                  onClick={() => setShowDisputeModal(false)}
                  className="p-2 hover:bg-muted rounded-lg transition-base"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-4 md:space-y-6">
                <Select
                  label={t?.disputeReason}
                  options={disputeReasons}
                  value={disputeReason}
                  onChange={setDisputeReason}
                  required
                />

                <Input
                  label={t?.disputeDetails}
                  type="text"
                  value={disputeDetails}
                  onChange={(e) => setDisputeDetails(e?.target?.value)}
                  placeholder={t?.disputePlaceholder}
                  required
                />

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    onClick={() => setShowDisputeModal(false)}
                  >
                    {t?.cancel}
                  </Button>
                  <Button
                    variant="destructive"
                    size="lg"
                    fullWidth
                    onClick={handleDisputeSubmit}
                    disabled={!disputeReason || !disputeDetails}
                  >
                    {t?.submitDispute}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRatingModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-200 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border border-border shadow-elevation-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                  {t?.ratingTitle}
                </h3>
                <button
                  onClick={() => setShowRatingModal(false)}
                  className="p-2 hover:bg-muted rounded-lg transition-base"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-sm md:text-base font-medium text-foreground mb-3">
                    {t?.ratingLabel}
                  </label>
                  <div className="flex gap-2 justify-center">
                    {[1, 2, 3, 4, 5]?.map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        className="p-2 hover:scale-110 transition-base"
                      >
                        <Icon
                          name={star <= rating ? 'Star' : 'Star'}
                          size={32}
                          color={star <= rating ? 'var(--color-warning)' : 'var(--color-muted-foreground)'}
                          strokeWidth={star <= rating ? 0 : 2}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <Input
                  label={t?.reviewLabel}
                  type="text"
                  value={review}
                  onChange={(e) => setReview(e?.target?.value)}
                  placeholder={t?.reviewPlaceholder}
                />

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    onClick={() => setShowRatingModal(false)}
                  >
                    {t?.cancel}
                  </Button>
                  <Button
                    variant="success"
                    size="lg"
                    fullWidth
                    onClick={handleRatingSubmit}
                    disabled={rating === 0}
                  >
                    {t?.submitReview}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionPanel;